import type { editor } from "monaco-editor"
import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import { applyInspectorEdit } from "@/v2-backoffice-client/layer/update/inspector/layer.update.inspector.utils"
import {
  buildPopupTemplateValue,
  getSelectedPopupFields,
} from "@/v2-backoffice-client/layer/update/inspector/layer.update.inspector.guided.utils"
import type { LayerServiceMetadata } from "@/v2-backoffice-client/layer/service/layer.service.metadata"

export type LayerUpdateInspectorGuidedPopupProps = {
  editor: editor.IStandaloneCodeEditor
  configuration: Record<string, unknown>
  metadata: LayerServiceMetadata
}

/**
 * Popup-field checklist: every field the service actually has, checked when
 * the draft's popupTemplate shows it. Toggling writes the complete
 * popupTemplate; entries the operator already customized keep their labels
 * and formats.
 */
function LayerUpdateInspectorGuidedPopup(
  props: LayerUpdateInspectorGuidedPopupProps,
) {
  const { editor: editorInstance, configuration, metadata } = props

  const selected = getSelectedPopupFields(configuration)

  function toggle(fieldName: string, checked: boolean) {
    const next = checked
      ? [...selected, fieldName]
      : selected.filter((name) => name !== fieldName)
    applyInspectorEdit(
      editorInstance,
      ["popupTemplate"],
      buildPopupTemplateValue(
        configuration,
        metadata.fields,
        next,
        metadata.title,
      ),
    )
  }

  return (
    <div className="flex max-h-52 flex-col gap-1 overflow-y-auto pr-1">
      {metadata.fields.map((field) => {
        const id = `guided-popup-${field.name}`
        return (
          <span key={field.name} className="flex items-center gap-2">
            <Checkbox
              id={id}
              checked={selected.includes(field.name)}
              onCheckedChange={(checked) => {
                toggle(field.name, checked === true)
              }}
            />
            <Label
              htmlFor={id}
              className="min-w-0 flex-1 cursor-pointer truncate text-xs font-normal"
              title={`${field.name}${field.alias && field.alias !== field.name ? ` — ${field.alias}` : ""}`}
            >
              {field.alias || field.name}
            </Label>
          </span>
        )
      })}
    </div>
  )
}

export default LayerUpdateInspectorGuidedPopup
